import { AsyncFilterMapper } from "@app/routing/mappers/AsyncFilterMapper";
import { IExtendedFilterValue } from "@app/interfaces/dtos/IExtendedFilterValue";
import { FilterType } from "@studyportals/omnisearch-interfaces";
import { RouteLocation } from "vue-router";
import { IExtendedWithPortalTypeFilterValue } from "@app/routing/mappers/IExtendedWithPortalTypeFilterValue";
import ISearchQueryFilter from "@domain/interfaces/dtos/ISearchQueryFilter";
import IExtendedSearchQueryFilter from "@app/interfaces/dtos/IExtendedSearchQueryFilter";
import { ISearchResult } from "@domain/interfaces/dtos/ISearchResult";
import { useAPIBaseUrl } from "@composables/useAPIBaseUrl";
import type { ICitySearchResult } from "@domain/interfaces/dtos/ICitySearchResult";
import { IExtendedCityFilterValue } from "@app/routing/mappers/cities/IExtendedCityFilterValue.ts";

export class CityFilterMapper extends AsyncFilterMapper {
	public filterType: FilterType = FilterType.CITY;
	public isMultiSelect = false; // TODO: set to true when multiple cities in query param are supported
	private static instance: CityFilterMapper;

	protected values: IExtendedCityFilterValue[] = [];

	public static getInstance(): CityFilterMapper {
		if (!CityFilterMapper.instance) {
			CityFilterMapper.instance = new CityFilterMapper();
		}
		return CityFilterMapper.instance;
	}

	private constructor() {
		super();
	}

	public canBeIndexed(): boolean {
		return false;
	}

	private extractCountryFilterFromProcessed(processedFilters?: IExtendedSearchQueryFilter[]): string | null {
		if (!processedFilters) return null;

		const countryFilter = processedFilters.find(f => f.type === FilterType.COUNTRY);
		if (!countryFilter || countryFilter.values.length === 0) return null;


		if(countryFilter.values.length === 1){
			return countryFilter.values[0]!.id;
		}

		return null;
	}

	private extractAreaFilterFromProcessed(processedFilters?: IExtendedSearchQueryFilter[]): string | null {
		if (!processedFilters) return null;

		const areaFilter = processedFilters.find(f => f.type === FilterType.AREA);
		if (!areaFilter || areaFilter.values.length !== 1) return null;

		return areaFilter.values[0]!.id;
	}

	private mapResultToValue(result: ICitySearchResult): IExtendedCityFilterValue {
		return {
			id: result.id.toString(),
			name: result.name,
			virtualPath: result.virtualPath,
			countryIsoCode: result.countryIsoCode,
			areaId: result.areaId?.toString() ?? null
		};
	}

	private async fetchCities(filters: ISearchQueryFilter[], virtualPath?: string): Promise<ISearchResult<ICitySearchResult>> {
		//MOCK FOR NOW
		return await $fetch('/api/search/filters/cities', {
			baseURL: useAPIBaseUrl(),
			method: 'POST',
			body: { filters, virtualPath }
		});
	}

	public override async getExtendedFilterValuesFromRoute(route: RouteLocation, processedFilters?: IExtendedSearchQueryFilter[]): Promise<IExtendedFilterValue[]> {
		const cityValues: IExtendedFilterValue[] = [];

		const paramValue = await this.retrieveCityDataFromParam(route, processedFilters);
		if (paramValue) cityValues.push(paramValue);

		const queryValues = await this.retrieveCityDataFromQuery(route);
		if (queryValues) cityValues.push(...queryValues);

		return cityValues;
	}

	public override getAllExtendedFilterValues(): IExtendedFilterValue[] {
		throw new Error('Not supported for CityFilterMapper');
	}

	public async getExtendedFilterValueByIdWithFetch(id: string): Promise<IExtendedFilterValue | undefined> {
		const existingValue = this.getExtendedFilterValueById(id);
		if (existingValue) return existingValue;

		const results = await this.fetchCities([{ type: this.filterType, values: [id] }]);
		if (results.results.length === 0) return;

		const value = this.mapResultToValue(results.results[0]!);

		this.addToCacheWithCap([value]);
		return value;
	}

	private async retrieveCityDataFromParam(route: RouteLocation, processedFilters?: IExtendedSearchQueryFilter[]): Promise<IExtendedFilterValue | undefined> {
		if (route.params?.[this.filterType] === undefined) return;

		const countryIsoCode = this.extractCountryFilterFromProcessed(processedFilters);
		if(!countryIsoCode) return;

		const virtualPath = route.params[this.filterType] as string;
		const areaId = this.extractAreaFilterFromProcessed(processedFilters);

		// A city attached to an area is only returned when that area is part of the filters
		const existingValue = this.values.find(v =>
			v.virtualPath === virtualPath &&
			v.countryIsoCode === countryIsoCode &&
			v.areaId === areaId
		);
		if (existingValue) return existingValue;

		const filters: ISearchQueryFilter[] = [{ type: FilterType.COUNTRY, values: [countryIsoCode] }];
		if (areaId) filters.push({ type: FilterType.AREA, values: [areaId] });

		const results = await this.fetchCities(filters, virtualPath);
		if (results.results.length === 0) return;

		const values = results.results.map(result => this.mapResultToValue(result));
		this.addToCacheWithCap(values.filter(value => !this.values.some(v => v.id === value.id)));

		return values.find(v => v.areaId === areaId);
	}

	private async retrieveCityDataFromQuery(route: RouteLocation): Promise<IExtendedWithPortalTypeFilterValue[]> {
		const values: IExtendedCityFilterValue[] = [];

		if (route.query?.[this.filterType] === undefined) return [];
		const allIds = (route.query[this.filterType] as string).split(',');
		const ids = [];

		for (const id of allIds) {
			const value = this.values.find(v => v.id === id);
			if (value) {
				values.push(value);
			} else {
				ids.push(id);
			}
		}

		if (ids.length === 0) return values;

		const valuesToCache = [];
		const results = await this.fetchCities([{ type: this.filterType, values: ids }]);
		for (const result of results.results) {
			const value = this.mapResultToValue(result);

			valuesToCache.push(value);
			values.push(value);
		}

		this.addToCacheWithCap(valuesToCache);
		return values;
	}
}
